import { TextField, Autocomplete, Box, Chip } from "@mui/material";

import { useCountryListing } from "../../api/queries/network/useCountryListing";

export type CountryOption = {
  label: string;
  value: number;
  flagSrc: string;
};

type CountryAutocompleteProps = {
  multiple?: boolean;
  limitTags?: number;
  value?: CountryOption | CountryOption[] | null;
  onChange?: (value: any) => void;
  error?: boolean;
  sx?: any;
};

const CountryAutocomplete = ({
  multiple = false,
  limitTags = 2,
  value,
  onChange,
  error,
  sx,
  ...props
}: CountryAutocompleteProps) => {
  const { countries, isCountriesDataLoading } = useCountryListing();

  const countryList: CountryOption[] = countries.map((c: any) => ({
    value: c.value,
    label: c.label,
    flagSrc: c.flagSrc,
  }));

  return (
    <Autocomplete
      {...props}
      multiple={multiple}
      limitTags={limitTags}
      disablePortal
      disabled={isCountriesDataLoading}
      options={countryList}
      value={value ?? (multiple ? [] : null)}
      onChange={(_, newValue) => {
        onChange?.(newValue);
      }}
      isOptionEqualToValue={(option, val) => option.value === val.value}
      renderInput={(params) => <TextField {...params} error={error} sx={sx} />}
      renderOption={(props, option: CountryOption) => (
        <Box component="li" {...props} sx={styles.option}>
          <Box component="img" src={option.flagSrc} alt={option.label} sx={styles.flag} />
          {option.label}
        </Box>
      )}
      renderTags={(values, getTagProps) =>
        values.map((option, index) => (
          <Chip
            sx={styles.autocompleteChip}
            label={
              <Box component="li" sx={styles.autocompleteChipLabel}>
                <Box component="img" src={option.flagSrc} alt={option.label} sx={styles.flag} />
                <Box
                  component="span"
                  sx={{ maxWidth: "70px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                >
                  {option.label}
                </Box>
              </Box>
            }
            {...getTagProps({ index })}
          />
        ))
      }
      sx={styles.autocomplete}
    />
  );
};

export default CountryAutocomplete;

const styles = {
  autocomplete: {
    "& .MuiAutocomplete-input": {
      padding: "0 !important",
    },
    // Ensure the container grows and wraps tags correctly when focused
    "& .MuiInputBase-root": {
      height: "auto",
      flexWrap: "wrap",
    },
  },
  flag: {
    width: "32px",
    height: "24px",
    borderRadius: "4px",
  },
  option: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    color: "var(--Gray-900, #101828)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "24px",
  },
  autocompleteChipLabel: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    color: "var(--Gray-700, #344054)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  },
  autocompleteChip: {
    borderRadius: "6px",
    margin: "6px 2px !important",
    border: "1px solid var(--Gray-300, #D0D5DD)",
    background: "var(--Base-White, #FFF)",
    gap: "4px",
    padding: "2px 5px",

    "& .MuiChip-label": {
      padding: 0,
    },
    "& .MuiChip-deleteIcon": {
      margin: 0,
    },
  },
};
